import { GameObject3D } from './../../../e.elements/GameElements/classes/types'
import TWEEN from '@tweenjs/tween.js'
import * as THREE from 'three'
import { AnimationAction, AnimationActionLoopStyles, LoopOnce, LoopRepeat, Object3D, Quaternion, Vector3 } from 'three'
import { GLTFActions } from '../../../f.assets/animations/CharactersAnimations'
import GameObject from '../../../e.elements/GameElements/classes/GameObject'
import Minion from '../../../e.elements/GameElements/classes/Minion'
import { CHARACTER_ACTION } from '../../../f.assets/Characters/scripts/constants'
import { useSceneStore } from '../../../a.scenes/GameScene/store'

export type Point = Record<'x' | 'y', number>
type Target = Record<'position' | 'rotation', number>
// const DURATION = 500*1.95
const BLEND_DURATION = 0.25

export default class CharacterAnimator {
  object: GameObject
  currentActionName?: keyof GLTFActions
  // currentAction?: AnimationAction
  actions?: GLTFActions

  constructor(object: GameObject) {
    this.object = object
  }

  play(
    actionName: keyof GLTFActions,
    loop: AnimationActionLoopStyles = LoopRepeat,
    outBlendDuration = BLEND_DURATION,
    inBlendDuration = BLEND_DURATION,
  ) {
    if (!this.actions) {
      const _actions = useSceneStore.getState().getGameElementsAnimationActions()
      this.actions = _actions.get(this.object.uuid) as GLTFActions
    }

    if (actionName === this.currentActionName) {
      // console.log('already playing ', actionName)
      return false
    }

    if(!this.actions) {
      console.warn(`Model has no actions`)
      return false
    }
    const newAction = this.actions[actionName]
    newAction.reset()
    newAction.fadeIn(inBlendDuration)
    newAction.setLoop(loop, Infinity)

    newAction.clampWhenFinished = loop === LoopOnce

    newAction.play()

    this.actions[this.currentActionName!]?.fadeOut(outBlendDuration)

    this.currentActionName = actionName
  }

  stop() {
    if (this.currentActionName && this.actions) {
      this.actions[this.currentActionName]?.fadeOut(BLEND_DURATION)
      this.currentActionName = 'BasePose'
    }
  }

  shouldRun() {
    const actionName = this.object.obj?.userData.tickDivisions.length > 1 ? 'Run' : 'Walk'
    if (this.currentActionName !== actionName) {
      // this.stop()
      this.play(actionName)
    }
  }

  idle() {
    if (this.currentActionName !== 'Idle') {
      // this.stop()
      this.play('Idle')
    }
  }

  async shouldIdle(actions: GLTFActions, isLast: boolean) {
    const minion = this.object as Minion
  }

  async playLooting() {
    if (this.currentActionName !== 'Climbing') {
      // this.stop()
      this.play('Climbing')
    }
  }

  async playAttack() {
    const attackVariants = ['Attack(1h)', 'AttackCombo', 'HeavyAttack', 'AttackSpinning']
    const move = attackVariants[Math.floor(Math.random() * attackVariants.length)] as keyof GLTFActions
    this.play(move)
  }

  playCheer() {
    this.play('Cheer')
  }

  playPick() {
    return new Promise(res => {
      this.play('PickUp', THREE.LoopOnce)
      setTimeout(res, 500)
    })
  }

  playDefeat() {
    return new Promise(res => {
      this.play('Defeat', THREE.LoopOnce, 0)
      setTimeout(res, 1000)
    })
  }

  lookAtPoint = (lookAtPoint: Vector3) => {
    const mesh = this.object.obj
    const positionDest = new THREE.Vector3()
    positionDest.copy(mesh.position)
    positionDest.add(lookAtPoint)
    this.lookAt(positionDest)
  }

  lookAt(lookAtPosition: Vector3) {
    const mesh = this.object.obj
    const helperObject = new THREE.Object3D()
    helperObject.position.copy(mesh.position)
    helperObject.lookAt(lookAtPosition)
    if (helperObject.quaternion.equals(mesh.quaternion)) {
      return true
    }
    return this.rotateTowards(helperObject.quaternion)
  }

  rotateTowards(targetQuaternion: Quaternion) {
    const mesh = this.object.obj
    const duration = mesh.userData.tickDivisions.length > 1 ? 500 : 1000
    const target = {
      t: 0,
    }
    return new Promise(res => {
      new TWEEN.Tween({ t: target.t })
        .to({ t: 1 }, duration)
        .onUpdate(partial => {
          mesh.quaternion.slerp(targetQuaternion, partial.t)
        })
        .onComplete(res)
        .start()
    })
  }

  moveV(target: Target, factor: number, DURATION: number) {
    const object = this.object.obj
    return new Promise(res => {
      new TWEEN.Tween({
        position: target.position,
      })
        .to(
          {
            position: target.position + 1 * factor,
          },
          DURATION,
        )
        .onUpdate(partial => {
          object.position.setZ(partial.position)
        })
        .onComplete(res)
        .start()
    })
  }

  moveH(target: Target, factor: number, DURATION: number) {
    const object = this.object.obj
    return new Promise(res => {
      new TWEEN.Tween({
        position: target.position,
      })
        .to(
          {
            position: target.position - 1 * factor,
          },
          DURATION,
        )
        .onUpdate(partial => {
          object.position.setX(partial.position)
        })
        .onComplete(res)
        .start()
    })
  }

  async moveLeft(actions: GLTFActions | null, isLast: boolean, DURATION: number) {
    const object = this.object.obj
    const lookTargetPoint = new THREE.Vector3(-1, 0, 0)

    this.lookAtPoint(lookTargetPoint)
    // if (actions) {
    this.shouldRun()
    // }

    const target = {
      position: object.position.x,
      rotation: object.rotation.z,
      jump: object.position.y,
    }

    await this.moveH(target, 1, DURATION)

    // if (actions) {
    //   this.shouldIdle(actions, isLast)
    // }
  }

  async moveRight(actions: GLTFActions | null, isLast: boolean, DURATION: number) {
    const object = this.object.obj
    const lookTargetPoint = new THREE.Vector3(1, 0, 0)
    this.lookAtPoint(lookTargetPoint)
    // if (actions) {
    this.shouldRun()
    // }

    const target = {
      position: object.position.x,
      rotation: object.rotation.z,
      jump: object.position.y,
    }

    await this.moveH(target, -1, DURATION)

    // if (actions) {
    //   this.shouldIdle(actions, isLast)
    // }
  }

  async moveUp(actions: GLTFActions | null, isLast: boolean, DURATION: number) {
    const object = this.object.obj
    const lookTargetPoint = new THREE.Vector3(0, 0, -1)
    this.lookAtPoint(lookTargetPoint)
    // if (actions) {
    this.shouldRun()
    // }

    const target = {
      position: object.position.z,
      rotation: object.rotation.x,
      jump: object.position.y,
    }

    await this.moveV(target, -1, DURATION)

    // if (actions) {
    //   this.shouldIdle(actions, isLast)
    // }
    // move(mesh, 'V', target, -1)
  }
  async moveDown(actions: GLTFActions | null, isLast: boolean, DURATION: number) {
    const object = this.object.obj

    const lookTargetPoint = new THREE.Vector3(0, 0, 1)
    this.lookAtPoint(lookTargetPoint)
    // if (actions) {
    this.shouldRun()
    // }

    const target = {
      position: object.position.z,
      rotation: object.rotation.x,
      jump: object.position.y,
    }

    await this.moveV(target, 1, DURATION)

    // if (actions) {
    //   this.shouldIdle(actions, isLast)
    // }
  }

  async bury(DURATION: number = 500) {
    const obj = this.object.obj
    return new Promise(res => {
      new TWEEN.Tween(obj.position).to({ y: -2.5 }, DURATION).onComplete(res).start()
    })
  }
  async digUp(DURATION: number = 500) {
    const obj = this.object.obj
    return new Promise(res => {
      new TWEEN.Tween(obj.position).to({ y: 0 }, DURATION).onComplete(res).start()
    })
  }

  async performAttack() {
    await new Promise(res => setTimeout(res, 1000))
    return true
  }
}
